import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { typography } from '../../../styles/typography';
import { IconButton, PrimaryButton } from '../../ui';
import {
  NO_SECONDARY_LOCATION,
  buildClubHouseFloorOptions,
  buildLocationItems,
  defaultAmenityDetails,
  initialLocationForAmenity,
  isBookableAmenity,
  isClubHouseAmenity,
  isGardenAmenity,
  resolveClubHouseFloorValue,
} from './amenityDetailUtils';

const FIELD_INPUT_CLASS =
  'w-full rounded-lg border border-neutral-300 bg-white px-3 py-2 text-sm text-neutral-700 focus:border-green-500 focus:ring-2 focus:ring-green-500/40';
const FIELD_LABEL_CLASS = 'text-sm font-medium text-gray-700';

const AmenityDetailSheet = ({ amenity, locationOptions, clubHouse, onSave, onClose }) => {
  const isCustom = Boolean(amenity.template?.isCustom);
  const isClubHouse = isClubHouseAmenity(amenity.template);
  const isGarden = isGardenAmenity(amenity.template);
  const isBookable = isBookableAmenity(amenity.template);

  const [displayName, setDisplayName] = useState(amenity.displayName || amenity.template.name);
  const [location, setLocation] = useState(() =>
    initialLocationForAmenity({ amenity, clubHouse, locationOptions })
  );
  const [details, setDetails] = useState(() => ({
    ...defaultAmenityDetails(),
    ...amenity.details,
  }));

  const locationItems = useMemo(
    () =>
      buildLocationItems({
        locationOptions,
        clubHouse,
        isClubHouse,
      }),
    [locationOptions, clubHouse, isClubHouse]
  );

  const clubHouseFloorOptions = useMemo(
    () => (clubHouse ? buildClubHouseFloorOptions(clubHouse) : []),
    [clubHouse]
  );

  const clubHouseFloorValue = clubHouse
    ? resolveClubHouseFloorValue(location, clubHouse)
    : location;

  const secondaryLocationOptions = useMemo(
    () => [NO_SECONDARY_LOCATION, ...locationOptions.filter(o => o !== location)],
    [locationOptions, location]
  );

  const secondaryLocationValue = (() => {
    const stored = details.secondaryLocation;
    if (stored && secondaryLocationOptions.includes(stored)) return stored;
    return NO_SECONDARY_LOCATION;
  })();

  const setDetail = (key, value) => {
    setDetails(prev => ({ ...prev, [key]: value }));
  };

  const handleSecondaryLocation = value => {
    if (value === NO_SECONDARY_LOCATION) {
      setDetails(prev => {
        const next = { ...prev };
        delete next.secondaryLocation;
        return next;
      });
      return;
    }
    setDetail('secondaryLocation', value);
  };

  const handleSave = () => {
    const trimmed = displayName.trim();
    onSave({
      template: amenity.template,
      displayName: trimmed || amenity.template.name,
      locationMapping: location,
      details: { ...details },
    });
  };

  const dialog = (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-2 sm:p-4 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[520px] h-auto max-h-[90vh] relative bg-neutral-50/50 rounded-xl shadow-[0px_15px_30px_0px_rgba(0,0,0,0.10)] shadow-[0px_5px_20px_0px_rgba(255,255,255,0.10)] shadow-[inset_0px_4px_25px_0px_rgba(255,255,255,0.35)] outline outline-1 outline-offset-[-1px] outline-neutral-300/20 backdrop-blur-[30px] overflow-hidden flex flex-col my-auto"
        onClick={e => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="amenity-detail-title"
      >
        <div className="px-6 sm:px-8 pt-4 sm:pt-6 pb-3 sm:pb-4 flex justify-between items-start flex-shrink-0">
          <div className="flex flex-col flex-1 min-w-0 pr-2">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 id="amenity-detail-title" className={typography.h3} style={{ color: '#000000' }}>
                {isCustom ? 'Custom Amenity' : amenity.template.name}
              </h3>
              {isCustom && (
                <span className="px-2 py-0.5 rounded-md bg-neutral-200 text-xs font-semibold text-neutral-600">
                  Custom
                </span>
              )}
            </div>
          </div>
          <IconButton
            onClick={onClose}
            className="flex-shrink-0"
            aria-label="Close"
            icon={
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M18 6L6 18M6 6L18 18"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            }
          />
        </div>

        <div className="px-4 sm:px-6 md:px-8 pb-4 sm:pb-6 flex-1 flex flex-col min-h-0 overflow-hidden">
          <div className="flex-1 overflow-y-auto pr-1 min-h-0 space-y-3">
            <label className="flex flex-col gap-1.5">
              <span className={FIELD_LABEL_CLASS}>Amenity Name</span>
              <input
                type="text"
                className={FIELD_INPUT_CLASS}
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
              />
            </label>

            {isClubHouse && clubHouse ? (
              <>
                <label className="flex flex-col gap-1.5">
                  <span className={FIELD_LABEL_CLASS}>Club House Floor</span>
                  <select
                    className={FIELD_INPUT_CLASS}
                    value={clubHouseFloorValue}
                    onChange={e => setLocation(e.target.value)}
                  >
                    {clubHouseFloorOptions.map(opt => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={FIELD_LABEL_CLASS}>Also map to (optional)</span>
                  <select
                    className={FIELD_INPUT_CLASS}
                    value={secondaryLocationValue}
                    onChange={e => handleSecondaryLocation(e.target.value)}
                  >
                    {secondaryLocationOptions.map(opt => (
                      <option key={opt} value={opt}>
                        {opt}
                      </option>
                    ))}
                  </select>
                </label>
              </>
            ) : (
              <label className="flex flex-col gap-1.5">
                <span className={FIELD_LABEL_CLASS}>Location Mapping</span>
                <select
                  className={FIELD_INPUT_CLASS}
                  value={locationItems.includes(location) ? location : locationItems[0]}
                  onChange={e => setLocation(e.target.value)}
                >
                  {locationItems.map(opt => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </label>
            )}

            {isGarden && (
              <>
                <label className="flex flex-col gap-1.5">
                  <span className={FIELD_LABEL_CLASS}>Garden Type</span>
                  <select
                    className={FIELD_INPUT_CLASS}
                    value={details.gardenType}
                    onChange={e => setDetail('gardenType', e.target.value)}
                  >
                    <option value="Central">Central</option>
                    <option value="Block-wise">Block-wise</option>
                  </select>
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={FIELD_LABEL_CLASS}>Area Size (optional)</span>
                  <input
                    type="text"
                    className={FIELD_INPUT_CLASS}
                    placeholder="e.g. 5000 sq.ft"
                    value={details.areaSize || ''}
                    onChange={e => setDetail('areaSize', e.target.value)}
                  />
                </label>
                <label className="community-advanced-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(details.irrigation)}
                    onChange={e => setDetail('irrigation', e.target.checked)}
                  />
                  Irrigation Available
                </label>
                <label className="community-advanced-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(details.seating)}
                    onChange={e => setDetail('seating', e.target.checked)}
                  />
                  Seating Available
                </label>
                <label className="community-advanced-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(details.lighting)}
                    onChange={e => setDetail('lighting', e.target.checked)}
                  />
                  Lighting Available
                </label>
              </>
            )}

            {isBookable && !isGarden && (
              <>
                <div className="community-amenity-timing-row">
                  <div>
                    <span className={FIELD_LABEL_CLASS}>Open Timing</span>
                    <p className="community-amenity-timing-value">
                      {details.openTime} – {details.closeTime}
                    </p>
                  </div>
                  <div className="community-amenity-timing-inputs">
                    <input
                      type="time"
                      className={FIELD_INPUT_CLASS}
                      value={details.openTime}
                      onChange={e => setDetail('openTime', e.target.value)}
                      aria-label="Open time"
                    />
                    <input
                      type="time"
                      className={FIELD_INPUT_CLASS}
                      value={details.closeTime}
                      onChange={e => setDetail('closeTime', e.target.value)}
                      aria-label="Close time"
                    />
                  </div>
                </div>
                <label className="flex flex-col gap-1.5">
                  <span className={FIELD_LABEL_CLASS}>Capacity</span>
                  <input
                    type="number"
                    min={1}
                    className={FIELD_INPUT_CLASS}
                    value={details.capacity}
                    onChange={e => setDetail('capacity', e.target.value)}
                  />
                </label>
                <label className="community-advanced-checkbox">
                  <input
                    type="checkbox"
                    checked={Boolean(details.bookingRequired)}
                    onChange={e => setDetail('bookingRequired', e.target.checked)}
                  />
                  Booking Required
                </label>
                <label className="flex flex-col gap-1.5">
                  <span className={FIELD_LABEL_CLASS}>Maintenance Frequency</span>
                  <select
                    className={FIELD_INPUT_CLASS}
                    value={details.maintenanceFrequency}
                    onChange={e => setDetail('maintenanceFrequency', e.target.value)}
                  >
                    <option value="Daily">Daily</option>
                    <option value="Weekly">Weekly</option>
                    <option value="Monthly">Monthly</option>
                  </select>
                </label>
                {isCustom && (
                  <label className="flex flex-col gap-1.5">
                    <span className={FIELD_LABEL_CLASS}>Notes (optional)</span>
                    <textarea
                      rows={2}
                      className={`${FIELD_INPUT_CLASS} resize-y`}
                      placeholder="Access rules, equipment, etc."
                      value={details.notes || ''}
                      onChange={e => setDetail('notes', e.target.value)}
                    />
                  </label>
                )}
              </>
            )}
          </div>

          <div className="mt-4 sm:mt-6 pt-4 border-t border-gray-200 flex gap-3 flex-shrink-0">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 min-h-12 rounded-lg border border-neutral-300 bg-white text-neutral-700 text-sm font-semibold hover:bg-neutral-50 transition-colors"
            >
              Cancel
            </button>
            <PrimaryButton
              onClick={handleSave}
              className="flex-1 min-h-12 h-12 w-auto min-w-0 text-base font-semibold"
            >
              Save Amenity
            </PrimaryButton>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
};

export default AmenityDetailSheet;
